import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MenubarView from './MenubarView';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.css';
import 'primeflex/primeflex.css';

const TestView = () => {
    const [agentHealth, setAgentHealth] = useState({ status: 'CHECKING', agent: 'aminappagent', model: 'gemini-3.7-flash' });
    const [loadingAction, setLoadingAction] = useState(null);
    const [testOutput, setTestOutput] = useState(null);

    // Form inputs for categorization test
    const [catDesc, setCatDesc] = useState('WHOLE FOODS MARKET #1029');
    const [catAmount, setCatAmount] = useState('65.50');
    const [catResult, setCatResult] = useState(null);

    // Check backend agent health on mount
    useEffect(() => {
        axios.get('/v1/inappagent/health')
            .then(res => setAgentHealth(res.data))
            .catch(() => setAgentHealth({ status: 'OFFLINE', error: 'Could not connect to /v1/inappagent/health' }));
    }, []);

    const runAgentTest = async (actionLabel, message) => {
        setLoadingAction(actionLabel);
        setTestOutput(null);
        try {
            const token = localStorage.getItem('bearerToken');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const startTime = performance.now();
            const res = await axios.post('/v1/inappagent/chat', { message }, { headers });
            const durationMs = Math.round(performance.now() - startTime);

            setTestOutput({
                action: actionLabel,
                query: message,
                durationMs,
                response: res.data
            });
        } catch (err) {
            setTestOutput({
                action: actionLabel,
                query: message,
                error: err.response?.data?.error || err.message
            });
        } finally {
            setLoadingAction(null);
        }
    };

    const runQuickCategorize = async () => {
        setLoadingAction('categorize');
        setCatResult(null);
        try {
            const amt = parseFloat(catAmount) || 20.0;
            const msg = `Categorize ${catDesc} $${amt.toFixed(2)}`;
            const token = localStorage.getItem('bearerToken');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.post('/v1/inappagent/chat', { message: msg }, { headers });
            setCatResult(res.data);
            setTestOutput({
                action: 'AI Categorization',
                query: msg,
                response: res.data
            });
        } catch (err) {
            setCatResult({ error: err.message });
        } finally {
            setLoadingAction(null);
        }
    };

    return (
        <div style={{ backgroundColor: '#f1f5f9', minHeight: '100vh', paddingBottom: '80px' }}>
            <MenubarView />

            <div className="p-grid p-justify-center" style={{ maxWidth: '1280px', margin: '24px auto', padding: '0 16px' }}>
                
                {/* Hero Header Card */}
                <div className="p-col-12">
                    <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <i className="pi pi-sparkles" style={{ fontSize: '1.8rem', color: '#2563eb' }}></i>
                                    <h1 style={{ margin: 0, fontSize: '1.6rem', color: '#0f172a', fontWeight: '700' }}>
                                        AM In-App Financial Agent Playground
                                    </h1>
                                </div>
                                <p style={{ margin: '8px 0 0 0', color: '#64748b', fontSize: '0.95rem' }}>
                                    Test page for evaluating <code>aminappagent</code>. Click the floating assistant icon in the bottom-right corner or use the scenario cards below to test real-time financial intelligence.
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <Tag 
                                    severity={agentHealth.status === 'ONLINE' ? 'success' : 'danger'} 
                                    value={`Agent: ${agentHealth.status}`} 
                                    style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                                />
                                <Tag 
                                    severity="info" 
                                    value={`Model: ${agentHealth.model || 'gemini-3.7-flash'}`} 
                                    style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                                />
                                <Button 
                                    label="Open Agent Drawer" 
                                    icon="pi pi-comments" 
                                    className="p-button-outlined p-button-primary"
                                    onClick={() => window.dispatchEvent(new CustomEvent('open-inapp-agent'))}
                                />
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Scenario Testing Cards Grid */}
                <div className="p-col-12 p-md-6 p-lg-3">
                    <Card title="💰 Cash Flow Intelligence" style={{ height: '100%', borderRadius: '10px' }}>
                        <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            Audit total inflows, expenditures, net cash flow, and savings rate.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                            <Button
                                label="2024 Cash Flow Audit"
                                icon="pi pi-chart-line"
                                className="p-button-sm p-button-outlined"
                                loading={loadingAction === 'cf-2024'}
                                onClick={() => runAgentTest('cf-2024', 'What was our total cash flow in 2024?')}
                            />
                            <Button
                                label="May 2024 Overview"
                                icon="pi pi-calendar"
                                className="p-button-sm p-button-outlined p-button-secondary"
                                loading={loadingAction === 'cf-may'}
                                onClick={() => runAgentTest('cf-may', 'Summarize cash flow for May 2024')}
                            />
                        </div>
                    </Card>
                </div>

                <div className="p-col-12 p-md-6 p-lg-3">
                    <Card title="🎯 Budget vs Actuals" style={{ height: '100%', borderRadius: '10px' }}>
                        <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            Compare monthly targets (<code>estimateentry</code>) with real spending.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                            <Button
                                label="Audit May 2024 Budget"
                                icon="pi pi-check-circle"
                                className="p-button-sm p-button-outlined p-button-success"
                                loading={loadingAction === 'budget-may'}
                                onClick={() => runAgentTest('budget-may', 'Compare budget vs actuals for May 2024')}
                            />
                            <Button
                                label="Top Spending Categories"
                                icon="pi pi-th-large"
                                className="p-button-sm p-button-outlined p-button-secondary"
                                loading={loadingAction === 'cats-top'}
                                onClick={() => runAgentTest('cats-top', 'Show top 5 spending categories in 2024')}
                            />
                        </div>
                    </Card>
                </div>

                <div className="p-col-12 p-md-6 p-lg-3">
                    <Card title="⚠️ Anomaly & Duplicate Scan" style={{ height: '100%', borderRadius: '10px' }}>
                        <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            Detect duplicate charges and category spending spikes (&gt; 2.5x avg).
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                            <Button
                                label="Scan for Duplicate Charges"
                                icon="pi pi-exclamation-triangle"
                                className="p-button-sm p-button-outlined p-button-warning"
                                loading={loadingAction === 'anom-dupe'}
                                onClick={() => runAgentTest('anom-dupe', 'Detect duplicate charges and spending spikes')}
                            />
                            <Button
                                label="Audit Financial Metadata"
                                icon="pi pi-list"
                                className="p-button-sm p-button-outlined p-button-secondary"
                                loading={loadingAction === 'meta-list'}
                                onClick={() => runAgentTest('meta-list', 'List all financial accounts and active categories')}
                            />
                        </div>
                    </Card>
                </div>

                <div className="p-col-12 p-md-6 p-lg-3">
                    <Card title="🏷️ AI Categorization" style={{ height: '100%', borderRadius: '10px' }}>
                        <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            Predict category via keyword rules, history, and Gemini AI.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                            <InputText 
                                value={catDesc} 
                                onChange={(e) => setCatDesc(e.target.value)} 
                                placeholder="Merchant / Memo"
                                className="p-inputtext-sm"
                            />
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <InputText 
                                    value={catAmount} 
                                    onChange={(e) => setCatAmount(e.target.value)} 
                                    placeholder="Amount ($)"
                                    className="p-inputtext-sm"
                                    style={{ width: '90px' }}
                                />
                                <Button
                                    label="Classify"
                                    icon="pi pi-sparkles"
                                    className="p-button-sm p-button-primary"
                                    loading={loadingAction === 'categorize'}
                                    onClick={runQuickCategorize}
                                    style={{ flex: 1 }}
                                />
                            </div>
                            {catResult && catResult.data && (
                                <div style={{ fontSize: '0.78rem', background: '#eff6ff', padding: '6px 8px', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                                    <strong>Category:</strong> <code>{catResult.data.category_name}</code> ({Math.round(catResult.data.confidence * 100)}%)
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Response / Inspector Output Panel */}
                <div className="p-col-12">
                    <Card title="📡 Test Output & Agent Response Inspector" style={{ borderRadius: '10px' }}>
                        {loadingAction && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px' }}>
                                <ProgressSpinner style={{ width: '32px', height: '32px' }} strokeWidth="4" />
                                <span style={{ color: '#64748b' }}>Running query with <code>aminappagent</code> via <code>ambackend</code>...</span>
                            </div>
                        )}

                        {!loadingAction && !testOutput && (
                            <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                                <i className="pi pi-terminal" style={{ fontSize: '2rem', marginBottom: '8px', display: 'block' }}></i>
                                Select a test scenario button above or open the in-app assistant to inspect live execution results.
                            </div>
                        )}

                        {!loadingAction && testOutput && (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' }}>
                                    <div>
                                        <Tag severity="info" value={testOutput.action} style={{ marginRight: '8px' }} />
                                        <span style={{ fontWeight: '600', color: '#1e293b' }}>Query: "{testOutput.query}"</span>
                                    </div>
                                    {testOutput.durationMs && (
                                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                            <i className="pi pi-clock" style={{ marginRight: '4px' }}></i>
                                            {testOutput.durationMs} ms
                                        </span>
                                    )}
                                </div>

                                {testOutput.response && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        {/* Rendered Reply */}
                                        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                                            <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#475569' }}>Formatted Agent Reply</h4>
                                            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: '0.88rem', color: '#1e293b' }}>
                                                {testOutput.response.reply}
                                            </pre>
                                            {testOutput.response.tools_used && (
                                                <div style={{ marginTop: '12px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Tools Executed:</span>
                                                    {testOutput.response.tools_used.map((t, idx) => (
                                                        <Tag key={idx} severity="warning" value={t} style={{ fontSize: '0.7rem' }} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Raw JSON payload */}
                                        <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px', overflow: 'auto', maxHeight: '320px' }}>
                                            <h4 style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#94a3b8' }}>Raw JSON Response</h4>
                                            <pre style={{ margin: 0, color: '#38bdf8', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                                {JSON.stringify(testOutput.response, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}

                                {testOutput.error && (
                                    <div style={{ padding: '16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#b91c1c' }}>
                                        <strong>Error:</strong> {testOutput.error}
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </div>

            </div>
        </div>
    );
};

export default TestView;